import React, { Dispatch } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Select, { ISelectOptions } from 'components/UI/Select';
import { IPosition, GroupsEnum } from 'types/users';
import AsyncSelect from 'components/UI/AsyncSelect';
import useFetch from 'use-http';
import styles from './styles.module.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSellerOnlyStores, useCashierOnlyStores, useMyGigSellerStores, useMyGigCashierStores } from 'hooks/useStoreFilters';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import { formatServiceTypeTitle } from 'utils/helpers';

interface IData {
	id: string;
	title: string;
}

export const convertToOption = (data: IData[]): ISelectOptions[] => {
	return data?.length
		? data.map((item: IData) => {
				return {
					value: String(item.id),
					label: formatServiceTypeTitle(item.title),
				};
		  })
		: [];
};

export interface FormInputs {
	userId?: string;
	firstname: string;
	secondname: string;
	middlename: string;
	document_series: string;
	document_number: string;
	counterparty?: string;
	counterpartyId?: string;
	position: string;
	positionId?: string;
	plannedStart?: string;
	plannedEnd?: string;
}

const UserForm = ({
	onSubmit,
	loadingData,
	defaultValues,
	mode = 'add',
	short = false,
}: {
	onSubmit: SubmitHandler<FormInputs>;
	loadingData?: boolean;
	defaultValues?: FormInputs;
	mode?: string;
	short?: boolean;
}) => {
	const { get, error } = useFetch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isMyShiftMode = searchParams.get('mode') === 'myshift';
	const isMyGigMode = searchParams.get('mode') === 'mygig';
	const isSellerOnlyStore = useSellerOnlyStores();
	const isCashierOnlyStore = useCashierOnlyStores();
	const isMyGigSellerStore = useMyGigSellerStores();
	const isMyGigCashierStore = useMyGigCashierStores();
	const user = useUnit($user);
	const hasOpeningManagerRole = user?.groups?.some(group => group === GroupsEnum.OPENING_MANAGER);

	const [initLoad, setInitLoad] = React.useState(false);

	const [counterpartyList, setCounterpartyList] = React.useState([]);
	const [positionOptions, setPositionOptions] = React.useState<ISelectOptions[]>([]);
	const { handleSubmit, control, watch, setValue } = useForm<FormInputs>({ defaultValues });

	React.useEffect(() => {
		init();
	}, []);

	async function init() {
		const resultCounterparty = await get(`/api/counterparty`);
		setCounterpartyList(resultCounterparty);
		setInitLoad(true);
		loadPosition();
	}

	async function loadPosition() {
		const data: IPosition[] = await get(`/api/positions`);
		const filterPosition = data.filter(pos => pos.position_id);
		const options: ISelectOptions[] = convertToOption(filterPosition);
		return setPositionOptions(options);
	}

	async function loadCounterparty(inputValue: string): Promise<ISelectOptions[]> {
		let data = [];
		const params = new URLSearchParams({
			title: inputValue,
		});
		if (inputValue) {
			data = await get(`/api/counterparty?${params.toString()}`);
		}

		const options = convertToOption(data);
		return options;
	}

	const myShiftOption = React.useMemo(() => {
		const option = (counterpartyList as IData[]).find(item => item.title === 'МОЯ СМЕНА');
		return option ? { value: String(option.id), label: option.title } : null;
	}, [counterpartyList]);

	const myGigOption = React.useMemo(() => {
		const option = (counterpartyList as IData[]).find(item => item.title === 'МАЙ ГИГ');
		return option ? { value: String(option.id), label: option.title } : null;
	}, [counterpartyList]);

	const getFilteredPositionOptions = (): ISelectOptions[] => {
		// Для роли OPENING_MANAGER доступны все должности
		if (hasOpeningManagerRole) {
			return positionOptions;
		}

		const cleanerOption = positionOptions.find(opt => opt.label === 'Уборка');
		const sellerOption = positionOptions.find(opt => opt.label === 'Обслуживание зал');
		const cashierOption = positionOptions.find(opt => opt.label === 'Обслуживание покупателей');

		if (isMyShiftMode) {
			const allowedOptions: ISelectOptions[] = [
				isSellerOnlyStore && sellerOption,
				isCashierOnlyStore && cashierOption,
			].filter((item): item is ISelectOptions => Boolean(item));
			return allowedOptions;
		}

		if (isMyGigMode) {
			const allowedOptions: ISelectOptions[] = [
				isMyGigSellerStore && sellerOption,
				isMyGigCashierStore && cashierOption,
			].filter((item): item is ISelectOptions => Boolean(item));
			return allowedOptions;
		}

		return cleanerOption ? [cleanerOption as ISelectOptions] : [];
	};

	const filteredPositionOptions = getFilteredPositionOptions();

	if (error) {
		return <>{typeof error === 'string' ? error : error.message}</>;
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
			<div className="row">
				<div className="column-xs-12">
					<Controller
						name={`firstname`}
						control={control}
						rules={{
							required: 'Обязательное поле',
							pattern: {
								value: /^\S+$/,
								message: 'Пробелы не допускаются',
							},
						}}
						render={({ field, fieldState }) => {
							return (
								<Input
									onChange={field.onChange}
									label={
										<span>
											Фамилия<span style={{ color: 'red' }}>*</span>
										</span>
									}
									onBlur={field.onBlur}
									name={field.name}
									error={fieldState.error}
									defaultValue={field.value}
								/>
							);
						}}
					/>
				</div>
				<div className="column-xs-12">
					<Controller
						name={`secondname`}
						control={control}
						rules={{
							required: 'Обязательное поле',
							pattern: {
								value: /^\S+$/,
								message: 'Пробелы не допускаются',
							},
						}}
						render={({ field, fieldState }) => {
							return (
								<Input
									onChange={field.onChange}
									label={
										<span>
											Имя <span style={{ color: 'red' }}>*</span>
										</span>
									}
									onBlur={field.onBlur}
									name={field.name}
									error={fieldState.error}
									defaultValue={field.value}
								/>
							);
						}}
					/>
				</div>
				<div className="column-xs-12">
					<Controller
						name={`middlename`}
						control={control}
						rules={{
							pattern: {
								value: /^\S+$/,
								message: 'Пробелы не допускаются',
							},
						}}
						render={({ field, fieldState }) => {
							return (
								<Input
									onChange={field.onChange}
									label="Отчество"
									onBlur={field.onBlur}
									name={field.name}
									error={fieldState.error}
									defaultValue={field.value}
								/>
							);
						}}
					/>
				</div>
				<div className="column-xs-12">
					{!watch('userId') && (
						<Controller
							name={`document_series`}
							control={control}
							render={({ field, fieldState }) => (
								<Input
									onChange={field.onChange}
									onBlur={field.onBlur}
									label="Серия удостоверения"
									name={field.name}
									error={fieldState.error}
									defaultValue={field.value}
								/>
							)}
						/>
					)}
				</div>
				<div className="column-xs-12">
					{!watch('userId') && (
						<Controller
							name={`document_number`}
							control={control}
							rules={{ required: 'Обязательное поле' }}
							render={({ field, fieldState }) => (
								<Input
									onChange={field.onChange}
									onBlur={field.onBlur}
									label={
										<span>
											Номер удостоверения<span style={{ color: 'red' }}>*</span>
										</span>
									}
									name={field.name}
									error={fieldState.error}
									defaultValue={field.value}
								/>
							)}
						/>
					)}
				</div>
				{!short && mode !== 'edit' && (
					<div className="column-xs-12">
						{initLoad && (
							<Controller
								name={`counterpartyId`}
								control={control}
								rules={{ required: 'Обязательное поле' }}
								render={({ field, fieldState }) => {
									if (isMyShiftMode) {
										// Устанавливаем значение "МОЯ СМЕНА" напрямую, если оно не установлено
										if (myShiftOption && !field.value) {
											field.onChange(myShiftOption);
											setValue('counterparty', myShiftOption.label);
											setValue('counterpartyId', myShiftOption.value);
										}

										if (!myShiftOption) {
											return <div style={{ color: 'red' }}>Ошибка: контрагент "МОЯ СМЕНА" не найден в списке</div>;
										}

										return (
											<Select
												options={[myShiftOption]}
												value={myShiftOption}
												onChange={(v: any) => {
													field.onChange(v);
													setValue('counterparty', myShiftOption.label);
													setValue('counterpartyId', myShiftOption.value);
												}}
												label={
													<span>
														Контрагент<span style={{ color: 'red' }}>*</span>
													</span>
												}
												error={fieldState.error}
												isDisabled={true}
											/>
										);
									}

									if (isMyGigMode) {
										// Устанавливаем значение "МАЙ ГИГ" напрямую, если оно не установлено
										if (myGigOption && !field.value) {
											field.onChange(myGigOption);
											setValue('counterparty', myGigOption.label);
											setValue('counterpartyId', myGigOption.value);
										}

										if (!myGigOption) {
											return <div style={{ color: 'red' }}>Ошибка: контрагент "МАЙ ГИГ" не найден в списке</div>;
										}

										return (
											<Select
												options={[myGigOption]}
												value={myGigOption}
												onChange={(v: any) => {
													field.onChange(v);
													setValue('counterparty', myGigOption.label);
													setValue('counterpartyId', myGigOption.value);
												}}
												label={
													<span>
														Контрагент<span style={{ color: 'red' }}>*</span>
													</span>
												}
												error={fieldState.error}
												isDisabled={true}
											/>
										);
									}

									return (
										<AsyncSelect
											onChange={(v: any) => {
												setValue('counterparty', v?.label || '');
												field.onChange(v);
											}}
											load={loadCounterparty}
											error={fieldState.error}
											label={
												<span>
													Контрагент<span style={{ color: 'red' }}>*</span>
												</span>
											}
											isClearable
											defaultOptions={convertToOption(counterpartyList)}
											defaultValue={field.value}
											debounceTimeout={1000}
										/>
									);
								}}
							/>
						)}
					</div>
				)}
				{positionOptions.length > 0 && !short && mode !== 'edit' && (
					<div className="column-xs-12">
						<Controller
							name={`positionId`}
							control={control}
							rules={{ required: 'Обязательное поле' }}
							render={({ field, fieldState }) => {
								// Устанавливаем единственную доступную позицию автоматически (но не для OPENING_MANAGER)
								if (
									!hasOpeningManagerRole &&
									filteredPositionOptions.length === 1 &&
									field.value !== filteredPositionOptions[0].value
								) {
									setValue('positionId', filteredPositionOptions[0].value, { shouldValidate: true });
								}

								return (
									<Select
										onChange={field.onChange}
										options={filteredPositionOptions}
										label={
											<span>
												Тип услуги<span style={{ color: 'red' }}>*</span>
											</span>
										}
										error={fieldState.error}
										defaultValue={
											field.value ||
											(!hasOpeningManagerRole &&
											filteredPositionOptions.length === 1 &&
											filteredPositionOptions[0]?.value
												? filteredPositionOptions[0]?.value
												: undefined)
										}
										isDisabled={!hasOpeningManagerRole && filteredPositionOptions.length === 1}
									/>
								);
							}}
						/>
					</div>
				)}
				{hasOpeningManagerRole && (
					<>
						<div className="column-xs-12">
							<Controller
								name={`plannedStart`}
								control={control}
								rules={{ required: hasOpeningManagerRole ? 'Обязательное поле' : false }}
								render={({ field, fieldState }) => (
									<Input
										type="datetime-local"
										onChange={field.onChange}
										onBlur={field.onBlur}
										label={
											<span>
												Плановое время выхода<span style={{ color: 'red' }}>*</span>
											</span>
										}
										name={field.name}
										error={fieldState.error}
										defaultValue={field.value}
									/>
								)}
							/>
						</div>
						<div className="column-xs-12">
							<Controller
								name={`plannedEnd`}
								control={control}
								rules={{ required: hasOpeningManagerRole ? 'Обязательное поле' : false }}
								render={({ field, fieldState }) => (
									<Input
										type="datetime-local"
										onChange={field.onChange}
										onBlur={field.onBlur}
										label={
											<span>
												Плановое время завершения<span style={{ color: 'red' }}>*</span>
											</span>
										}
										name={field.name}
										error={fieldState.error}
										defaultValue={field.value}
									/>
								)}
							/>
						</div>
					</>
				)}
				<div className="column-xs-12">
					<div className="row">
						<div className="column-xs-6">
							<Button variant="violet" onClick={() => navigate('/qrcode')} style={{ margin: 'auto' }}>
								Назад
							</Button>
						</div>
						<div className="column-xs-6">
							<Button type="submit" style={{ margin: 'auto' }} disabled={loadingData}>
								{loadingData ? 'Загрузка...' : 'Подтвердить'}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</form>
	);
};

export default UserForm;
